import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SidebarItem = memo(({ item, isMinimized, depth = 0 }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const actions = [
    "add",
    "edit",
    "details",
    "view",
    "create",
    "update",
    "delete",
    "all",
  ];

  // Recursive function to determine if item or any of its children matches current path
  const isItemActiveRecursive = useCallback(
    (itemToCheck, currentPath) => {
      // Normalize paths for comparison
      const itemPath = itemToCheck.path?.startsWith("/")
        ? itemToCheck.path
        : `/${itemToCheck.path}`;
      const normalizedCurrentPath = currentPath.startsWith("/")
        ? currentPath
        : `/${currentPath}`;

      // 1. Direct match
      if (itemPath === normalizedCurrentPath) return true;

      // 2. Check activePaths array if it exists
      if (
        itemToCheck.activePaths?.some((p) => {
          const normalizedP = p.startsWith("/") ? p : `/${p}`;
          return (
            normalizedCurrentPath === normalizedP ||
            normalizedCurrentPath.startsWith(normalizedP + "/")
          );
        })
      ) {
        return true;
      }

      // 3. Match for action routes with URL params (e.g., /sales/pricelist/edit/123)
      const pathSegments = normalizedCurrentPath.split("/").filter(Boolean);
      if (itemToCheck.path && pathSegments.length >= 2) {
        const potentialAction = pathSegments[pathSegments.length - 2];
        if (actions.includes(potentialAction)) {
          const basePath = "/" + pathSegments.slice(0, -1).join("/");
          if (itemPath === basePath) return true;

          const parentPath = "/" + pathSegments.slice(0, -2).join("/");
          if (itemPath === parentPath) return true;
        }

        const action = pathSegments[pathSegments.length - 1];
        if (actions.includes(action)) {
          const basePath = "/" + pathSegments.slice(0, -1).join("/");
          if (itemPath === basePath) return true;
        }
      }

      // 4. Check children recursively
      if (itemToCheck.childrens) {
        return itemToCheck.childrens.some((child) =>
          isItemActiveRecursive(child, normalizedCurrentPath),
        );
      }

      return false;
    },
    [actions],
  );

  const currentPath = location.pathname;

  // Compute active state (for this item and propagate to parent)
  const isActive = useMemo(
    () => isItemActiveRecursive(item, currentPath),
    [item, currentPath, isItemActiveRecursive],
  );

  // Auto-expand if active and has children
  useEffect(() => {
    if (isMinimized) {
      setOpen(false);
    } else if (isActive && item.childrens) {
      setOpen(true);
    }
  }, [isActive, item.childrens, isMinimized]);

  const handleToggle = () => !isMinimized && setOpen((prev) => !prev);
  const handleNavigation = (e) => {
    e.preventDefault();
    navigate(item.path || "/loading");
  };
  const handleShortcutClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(item.shortcutPath);
  };

  // Helper classes
  const hasGrandchildren = item.childrens?.some(
    (child) => child.childrens?.length > 0,
  );
  const isParentOfInnermostChildren =
    depth === 1 && item.childrens && !hasGrandchildren;
  const isInnermostChild =
    depth === 2 && (!item.childrens || item.childrens.length === 0);

  const itemClasses = `sidebar-item ${open ? "open" : ""} ${isActive && depth === 0 ? "active" : ""} ${isParentOfInnermostChildren ? "parent-of-innermost" : ""}`;

  const titleClasses = `sidebar-title ftw-4 ${isParentOfInnermostChildren ? "blue" : "dk-gray"} common-text ${isActive && depth === 0 ? "active" : ""}`;

  const plainClasses = `sidebar-item plain ftw-4 dk-gray ${isActive ? "active" : ""} ${isInnermostChild ? "innermost-child" : ""}`;
  const isTopLevelWithIcon = depth === 0 && item.icon;

  // if (item.childrens || item.path === "/dashboard") {
  if (item.childrens) {
    return (
      <div className={itemClasses}>
        <div className={titleClasses} onClick={handleToggle}>
          {item.icon ? (
            <img
              src={`/assets/${item.icon}.png`}
              alt={item.title}
              className="sidebar-icon"
            />
          ) : item.childrens ? (
            <img
              src={
                isParentOfInnermostChildren
                  ? "/assets/black-dot.png"
                  : "/assets/dot-icon.png"
              }
              alt={item.title}
              className="sidebar-icon-dot"
            />
          ) : null}
          {!isMinimized && <span>{t(item.title)}</span>}
          {item.childrens && !isMinimized && (
            <img
              src="/assets/Expand.png"
              alt="toggle"
              className={`toggle-btn ${open ? "rotated" : ""}`}
            />
          )}
        </div>
        {item.childrens && open && (
          <div className="sidebar-content">
            {item.childrens.map((child, index) => (
              <SidebarItem
                key={child.path || index}
                item={child}
                isMinimized={isMinimized}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <a
      href={item.path || "/loading"}
      className={plainClasses}
      style={{
        ...(isInnermostChild ? { padding: "0.5rem 2rem" } : {}),
        ...(depth === 0 && (!item.childrens || item.childrens.length === 0)
          ? { gap: "0px" }
          : {}),
      }}
      onClick={handleNavigation}
    >
      {isTopLevelWithIcon ? (
        <img
          src={`/assets/${item.icon}.png`}
          alt={item.title}
          className="sidebar-icon"
        />
      ) : !isInnermostChild ? (
        <img
          src="/assets/black-dot.png"
          alt={item.title}
          className="sidebar-icon-dot"
        />
      ) : null}
      {!isMinimized && (
        <span
          style={{
            marginInlineEnd: isActive && item.shortcutPath ? "25px" : "0px",
          }}
        >
          {t(item.title)}
        </span>
      )}
      {!isMinimized && isActive && item.shortcutPath && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="21"
          viewBox="0 0 21 21"
          fill="none"
          className="sidebar-plus-img"
          onClick={handleShortcutClick}
        >
          <rect width="21" height="21" rx="10.5" fill="#094C80" />
          <path
            d="M6 10.5H15"
            stroke="white"
            strokeWidth="1.46625"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.5002 6V15"
            stroke="white"
            strokeWidth="1.46625"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </a>
  );
});

SidebarItem.displayName = "SidebarItem";
export default SidebarItem;
